import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Td,
  Tr,
  Box,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Checkbox,
  Menu,
  MenuButton,
  MenuList,
} from '@chakra-ui/react';
import { flexRender, Row } from '@tanstack/react-table';
import _ from 'lodash';
import React from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import {
  CalcTermsCategory,
  CalcTermsRow,
  formatCellValue,
  isCalcTermsDynamicFormulaArray,
  termKeyColumnId,
} from './calculationTerminologiesUtils';
import { colors } from 'packages/common-frontend/colors';
import { FormulaContainer } from 'packages/common-frontend/components/FormulaContainer';
import { Icon } from 'packages/common-frontend/components/icons';

const TableTd: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  return (
    <Td cursor="default" fontSize="sm" padding="16px" verticalAlign="top">
      {children}
    </Td>
  );
};

const TableFormulaArrayContent: React.FC<{
  formulas: any[];
  rowId: string; // only used for unique child key
}> = ({ formulas, rowId }) => {
  if (!isCalcTermsDynamicFormulaArray(formulas)) {
    return (
      <Td cursor="default" fontSize="sm" padding="16px">
        {formulas.toString()}
      </Td>
    );
  }

  const formulasByCreditFacility = _.groupBy(
    formulas,
    ({ creditFacilitySlug }) => creditFacilitySlug,
  );

  return (
    <>
      {_.map(
        formulasByCreditFacility,
        (formulasForCreditFacility, creditFacilitySlug) => {
          if (
            formulasForCreditFacility.length < 1 ||
            _.every(
              formulasForCreditFacility,
              ({ formula }) => formula === null,
            )
          ) {
            return null;
          }

          return (
            <Box
              key={`formula-array-content-${rowId}-${creditFacilitySlug}`}
              mb="16px"
            >
              <Text fontWeight={500} mb="8px">
                {`${commonUtils.creditFacilitySlugToName(creditFacilitySlug)}:`}
              </Text>
              {formulasForCreditFacility.length > 1 &&
                _.map(
                  formulasForCreditFacility,
                  ({ loanProductSlug, formula }) => {
                    if (!formula) {
                      return null;
                    }

                    return (
                      <Box
                        key={`formula-array-content-${rowId}-${creditFacilitySlug}-${loanProductSlug}`}
                        mb="16px"
                      >
                        <Text>{`${commonUtils.toTitleCase(
                          loanProductSlug,
                        )}:`}</Text>
                        <FormulaContainer>{formula}</FormulaContainer>
                      </Box>
                    );
                  },
                )}

              {formulasForCreditFacility.length === 1 && (
                <FormulaContainer>{formulas[0].formula}</FormulaContainer>
              )}
            </Box>
          );
        },
      )}
    </>
  );
};

export const FormulaContent: React.FC<{
  cellValue: any;
  rowId: string;
}> = ({ cellValue, rowId }) => {
  if (!cellValue) {
    return null;
  }
  if (Array.isArray(cellValue)) {
    return <TableFormulaArrayContent formulas={cellValue} rowId={rowId} />;
  }
  if (typeof cellValue === 'string') {
    return <FormulaContainer>{cellValue}</FormulaContainer>;
  }
  return null;
};

export const CalculationTerminologiesTableRow: React.FC<{
  row: Row<CalcTermsRow>;
}> = ({ row }) => {
  return (
    <Tr key={row.id}>
      {_.map(row.getVisibleCells(), (cell, index) => {
        const cellValue = cell.getValue();
        const columnId = cell.column.id;

        const formattedCellValue = formatCellValue({
          columnId,
          value: cellValue,
        });

        return (
          <TableTd key={`${row.id}-${index}`}>
            {columnId === termKeyColumnId && (
              <Text fontWeight={500}>
                {flexRender(formattedCellValue, cell.getContext())}
              </Text>
            )}
            {columnId === 'definition' && (
              <Text color={colors.black[70]}>
                {flexRender(formattedCellValue, cell.getContext())}
              </Text>
            )}
            {columnId === 'formula' && (
              <FormulaContent cellValue={cellValue} rowId={row.id} />
            )}
          </TableTd>
        );
      })}
    </Tr>
  );
};

export const CalculationTerminologiesSearchBar: React.FC<{
  globalFilter: string;
  setGlobalFilter: React.Dispatch<React.SetStateAction<string>>;
}> = ({ globalFilter, setGlobalFilter }) => {
  return (
    <Box
      borderRadius="8px"
      padding="16px"
      width="100%"
      backgroundColor={colors.background[100]}
    >
      <InputGroup backgroundColor="white">
        <InputLeftElement pointerEvents="none">
          <Icon icon="magnifying-glass" />
        </InputLeftElement>
        <Input
          value={globalFilter}
          onChange={(e) => {
            setGlobalFilter(_.toString(e.target.value));
          }}
          placeholder="Enter words to search"
        />
      </InputGroup>
    </Box>
  );
};

const onToggleTermsCategoryFilter = ({
  termsCategory,
  selectedTermsCategories,
  setSelectedTermsCategories,
}: {
  termsCategory: CalcTermsCategory;
  selectedTermsCategories: Set<CalcTermsCategory>;
  setSelectedTermsCategories: React.Dispatch<
    React.SetStateAction<Set<CalcTermsCategory>>
  >;
}) => {
  const isCategoryCurrentlySelected = selectedTermsCategories.has(
    termsCategory,
  );

  const newCategories = _.clone(selectedTermsCategories);

  switch (termsCategory) {
    case 'borrowingBaseCash':
      if (!isCategoryCurrentlySelected) {
        newCategories.add(termsCategory);
        if (selectedTermsCategories.has('borrowingBaseReceivables')) {
          newCategories.add('borrowingBaseReport');
        }
        setSelectedTermsCategories(newCategories);
      } else {
        newCategories.delete(termsCategory);
        newCategories.delete('borrowingBaseReport');
        setSelectedTermsCategories(newCategories);
      }
      break;

    case 'borrowingBaseReceivables':
      if (!isCategoryCurrentlySelected) {
        newCategories.add(termsCategory);
        if (selectedTermsCategories.has('borrowingBaseCash')) {
          newCategories.add('borrowingBaseReport');
        }
        setSelectedTermsCategories(newCategories);
      } else {
        newCategories.delete(termsCategory);
        newCategories.delete('borrowingBaseReport');
        setSelectedTermsCategories(newCategories);
      }
      break;

    case 'borrowingBaseReport':
      if (!isCategoryCurrentlySelected) {
        newCategories.add(termsCategory);
        newCategories.add('borrowingBaseCash');
        newCategories.add('borrowingBaseReceivables');
        setSelectedTermsCategories(newCategories);
      } else {
        newCategories.delete(termsCategory);
        newCategories.delete('borrowingBaseCash');
        newCategories.delete('borrowingBaseReceivables');
        setSelectedTermsCategories(newCategories);
      }
      break;

    case 'metrics':
      if (!isCategoryCurrentlySelected) {
        newCategories.add(termsCategory);

        setSelectedTermsCategories(newCategories);
      } else {
        newCategories.delete(termsCategory);
        setSelectedTermsCategories(newCategories);
      }
      break;
    default:
      setSelectedTermsCategories(selectedTermsCategories);
  }
};

export const CalculationTerminologiesMenuFilter: React.FC<{
  selectedTermsCategories: Set<CalcTermsCategory>;
  setSelectedTermsCategories: React.Dispatch<
    React.SetStateAction<Set<CalcTermsCategory>>
  >;
}> = ({ selectedTermsCategories, setSelectedTermsCategories }) => {
  return (
    <Menu
      matchWidth
      autoSelect={false}
      eventListeners={{ scroll: false, resize: true }}
    >
      <MenuButton>
        <ChevronDownIcon />
      </MenuButton>
      <MenuList
        zIndex="dropdown"
        display="grid"
        padding="24px"
        background={colors.background[50]}
        borderRadius="4px"
        border="none"
        box-shadow="0px 1px 2px 0px rgba(0, 0, 0, 0.30)"
      >
        <Text fontWeight={500} fontSize="16px" mb="16px">
          Terms
        </Text>
        <Checkbox
          isChecked={
            selectedTermsCategories.has('borrowingBaseCash') &&
            selectedTermsCategories.has('borrowingBaseReceivables')
          }
          onChange={() =>
            onToggleTermsCategoryFilter({
              setSelectedTermsCategories,
              selectedTermsCategories,
              termsCategory: 'borrowingBaseReport',
            })
          }
          padding="8px"
          fontSize="16px"
          fontWeight={400}
        >
          Borrowing Base Report
        </Checkbox>
        <Checkbox
          isChecked={selectedTermsCategories.has('borrowingBaseCash')}
          onChange={() =>
            onToggleTermsCategoryFilter({
              setSelectedTermsCategories,
              selectedTermsCategories,
              termsCategory: 'borrowingBaseCash',
            })
          }
          ml="32px"
          padding="8px"
          fontSize="16px"
          fontWeight={400}
        >
          Borrowing Base Cash
        </Checkbox>
        <Checkbox
          isChecked={selectedTermsCategories.has('borrowingBaseReceivables')}
          onChange={() =>
            onToggleTermsCategoryFilter({
              setSelectedTermsCategories,
              selectedTermsCategories,
              termsCategory: 'borrowingBaseReceivables',
            })
          }
          ml="32px"
          padding="8px"
          fontSize="16px"
          fontWeight={400}
        >
          Borrowing Base Receivables
        </Checkbox>
        <Checkbox
          isChecked={selectedTermsCategories.has('metrics')}
          onChange={() =>
            onToggleTermsCategoryFilter({
              setSelectedTermsCategories,
              selectedTermsCategories,
              termsCategory: 'metrics',
            })
          }
          padding="8px"
          fontSize="16px"
          fontWeight={400}
        >
          Metrics
        </Checkbox>
      </MenuList>
    </Menu>
  );
};
